/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { BlIcon, BlIconSize, BlIconType } from '@blend/shared-react';
import { css, StyleDeclaration, StyleSheet } from 'aphrodite';
import { colors, IconButton, IconButtonType, media, mixins, variables } from '@blend/consumer-components';
import { useConsumerTheme } from '../../hooks/useConsumerTheme';

const defaultPadding = 15;

const styles: StyleDeclaration = StyleSheet.create({
  AlertBox: {
    display: 'flex',
    borderRadius: variables.globalBorderRadius,
    padding: defaultPadding,
    color: colors.clayPlus4,
    fontSize: 14,
  },

  topIconAlignment: {
    alignItems: 'flex-start',
  },

  centerIconAlignment: {
    alignItems: 'center',
  },

  bottomBorder: {
    ...mixins.borderBottom(2, 'solid', 'inherits'),
  },

  borderUnrounded: {
    borderRadius: 0,
  },

  iconWrapper: {
    lineHeight: 1,
    marginRight: 8,
    fontSize: 20,
  },

  closeButtonWrapper: {
    marginLeft: 'auto',
  },

  info: {
    color: colors.steel,
    backgroundColor: colors.aliceishBlue,
  },

  infoIconWrapper: {
    color: colors.aliceishBluePlus1,
  },

  error: {
    color: colors.brickPlus2,
    backgroundColor: colors.brickMinus10,
  },

  warning: {
    color: colors.darkerGray,
    background: colors.mustard,
  },

  warningIconWrapper: {
    color: colors.mustard,
    [media.mobileMedium]: {
      display: 'none',
    },
  },

  /**
   * Warning border is special because the text color is darkerGray for 508 compliance reason.
   * As a result, we need to sepecify the border color here.
   */
  warningBorderColor: {
    borderColor: colors.mustard,
  },

  inheritsBorderColor: {
    borderColor: 'inherits',
  },
});

export type AlertBoxBorderColorType = 'inherits' | 'warning';

export interface AlertBoxProps {
  alignIcon?: 'top' | 'center';
  borderColor?: AlertBoxBorderColorType;
  icon: BlIconType;
  iconSize?: BlIconSize;
  isBorderRounded?: boolean;
  type: IconButtonType;
  onClose?(): void;
  hideBottomBorder?: boolean;
}

export const AlertBox: React.FC<AlertBoxProps> = ({ ...props }) => {
  const { appConfig } = useConsumerTheme();
  return (
    <div
      className={css(
        styles.AlertBox,
        styles[props.type],
        styles[props.alignIcon ? props.alignIcon : 'center' + 'IconAlignment'],
        !props.hideBottomBorder && styles.bottomBorder,
        styles[props.borderColor ? props.borderColor : 'inherits' + 'BorderColor'],
        !props.isBorderRounded && styles.borderUnrounded,
      )}
    >
      <div className={css(styles.iconWrapper, styles[props.type + 'IconWrapper'])}>
        <BlIcon icon={props.icon} size={props.iconSize} />
      </div>
      <span>{props.children}</span>
      {props.onClose && (
        <div className={css(styles.closeButtonWrapper)}>
          <IconButton icon="delete" onClick={props.onClose} color={props.type} actionColor={appConfig.colors.action} />
        </div>
      )}
    </div>
  );
};
